import type { SupabaseClient } from "@supabase/supabase-js";
import imageUtils from "@/lib/imageUtils";
import { trackMetaSubmitListing } from "@/lib/metaPixel";
import { trackClaritySubmitListing } from "@/lib/clarity";
import type { Database } from "@/lib/database.types";
import type { ItemImage } from "@/lib/types";
import { removeSellListingItem } from "@/lib/listing/createSellListingCore";
import {
  assertListingPhotoUniqueness,
  computeListingImageContentHashFromBlob,
} from "@/lib/listing/listingPhotoDuplicateCheck";

export type CompleteSellListingImagesParams = {
  supabase: SupabaseClient<Database>;
  itemId: string;
  persistedTitle: string;
  numericPrice: number;
  uploadedImages: ItemImage[];
  /** When set, admin listing-submitted is deferred to batch completion on the client. */
  listingBatchId?: string | null;
};

export async function completeSellListingImages(
  params: CompleteSellListingImagesParams,
): Promise<void> {
  const { supabase, itemId, persistedTitle, numericPrice, uploadedImages } =
    params;

  const mainImage = uploadedImages.find((img) => img.isMain);
  const mainImageIndex = mainImage ? uploadedImages.indexOf(mainImage) : 0;
  const orderedImages = [...uploadedImages];
  if (mainImageIndex > 0) {
    const [mainImageToMove] = orderedImages.splice(mainImageIndex, 1);
    orderedImages.unshift(mainImageToMove);
  }

  try {
    const preUploadHashes = await Promise.all(
      orderedImages.map((image) => {
        if (!image.file) {
          throw new Error(
            "One or more images are invalid. Please try uploading them again.",
          );
        }
        return computeListingImageContentHashFromBlob(image.file);
      }),
    );
    assertListingPhotoUniqueness(preUploadHashes);

    const uploadedHashes: string[] = [];
    for (let i = 0; i < orderedImages.length; i++) {
      const image = orderedImages[i];
      if (!image.file) {
        console.error("Image file is missing");
        throw new Error(
          "One or more images are invalid. Please try uploading them again.",
        );
      }
      const uploadedImage = await imageUtils.uploadItemImage({
        file: image.file,
        itemId,
        isMain: i === 0,
        metadata: { position: i },
        skipCompression: true,
      });
      if (!uploadedImage) {
        throw new Error("Failed to upload image");
      }
      if (!uploadedImage.hash) {
        throw new Error("Could not fingerprint a photo. Try uploading again.");
      }
      uploadedHashes.push(uploadedImage.hash);
    }

    assertListingPhotoUniqueness(uploadedHashes);
  } catch (err) {
    console.error("Error processing image:", err);
    const uploadError =
      err instanceof Error
        ? err
        : new Error("Failed to process and upload image. Please try again.");
    try {
      await removeSellListingItem(supabase, itemId);
    } catch (cleanupErr) {
      console.error(
        "Failed to roll back listing after image error:",
        cleanupErr,
      );
      const cleanupMessage =
        cleanupErr instanceof Error
          ? cleanupErr.message
          : `Could not clean up listing ${itemId}.`;
      throw new Error(`${uploadError.message} ${cleanupMessage}`);
    }
    throw uploadError;
  }

  trackMetaSubmitListing({
    itemId,
    title: persistedTitle,
    valueNgn: numericPrice,
  });
  trackClaritySubmitListing({
    itemId,
    title: persistedTitle,
    valueNgn: numericPrice,
  });

  const {
    data: { session: listingSession },
  } = await supabase.auth.getSession();
  if (listingSession?.access_token && !params.listingBatchId) {
    try {
      await fetch("/api/notifications/events/listing-submitted", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${listingSession.access_token}`,
        },
        body: JSON.stringify({ itemId }),
      });
    } catch (notifyErr) {
      console.error("listing-submitted notification:", notifyErr);
    }
  }
}
